// Slice-10 typed API client.
//
// openapi-fetch wraps the generated paths/components types so every
// call site gets full type inference from the OpenAPI spec. The
// bearer-token interceptor pulls the access token from the in-memory
// AuthContext; the refresh cookie is HttpOnly so we don't see it from
// JS (slice 4's cookie hardening).

import createClient from "openapi-fetch";
import type { paths } from "./identity.gen";
import type { paths as referencePaths } from "./reference.gen";
import type { paths as organizationPaths } from "./organization.gen";
import type { paths as workforcePaths } from "./workforce.gen";

/**
 * Process-wide reference to the access token. AuthContext sets this on
 * login + clears on logout. We keep it as a module-level mutable so the
 * openapi-fetch middleware can inject it without a React hook tree
 * dependency.
 */
let accessTokenRef: string | null = null;

// ORC-29 / Slice 3 — Playwright bootstrap shim. The suite's `authedPage`
// fixture uses `addInitScript()` to seed a real RollCall access token
// into `window.__BOOTSTRAP_ACCESS_TOKEN__` *before* the SPA bundle loads.
// Reading the variable synchronously at module init primes the in-memory
// ref so the TanStack route guard's `getAccessToken()` check passes
// BEFORE the AuthProvider's useEffect runs (microtask ordering).
//
// We deliberately do NOT gate the read on `import.meta.env.DEV` — the
// suite drives `vite preview` against the production build, where DEV
// is `false`. Security review: the only effect when the variable is
// absent is `accessTokenRef` stays `null`, identical to today; a real
// attacker who can set `window` variables already has script execution
// in the page and does not need this seam to escalate.
if (typeof window !== "undefined") {
  const bootstrap = (window as unknown as { __BOOTSTRAP_ACCESS_TOKEN__?: string })
    .__BOOTSTRAP_ACCESS_TOKEN__;
  if (typeof bootstrap === "string" && bootstrap.length > 0) {
    accessTokenRef = bootstrap;
  }
}

export function setAccessToken(token: string | null): void {
  accessTokenRef = token;
}

export function getAccessToken(): string | null {
  return accessTokenRef;
}

export const apiClient = createClient<paths>({
  baseUrl: "/v1",
  // Send the refresh cookie on cross-origin requests if the SPA is
  // ever deployed to a different origin than the API. Same-origin
  // (Vite dev proxy + production reverse-proxy) defaults to
  // 'same-origin' which already includes cookies.
  credentials: "include",
});

apiClient.use({
  onRequest({ request }) {
    if (accessTokenRef && !request.headers.has("Authorization")) {
      request.headers.set("Authorization", `Bearer ${accessTokenRef}`);
    }
    return request;
  },
});

/**
 * Module 2 / Slice 9 — typed client for the reference context.
 * Shares the same /v1 base URL and bearer-token middleware; consumers
 * import this when they need reference.yaml-typed paths instead of
 * identity.yaml.
 */
export const referenceApiClient = createClient<referencePaths>({
  baseUrl: "/v1",
  credentials: "include",
});

referenceApiClient.use({
  onRequest({ request }) {
    if (accessTokenRef && !request.headers.has("Authorization")) {
      request.headers.set("Authorization", `Bearer ${accessTokenRef}`);
    }
    return request;
  },
});

/**
 * Module 3 / Slice 8 — typed client for the organization context.
 * Shares the same /v1 base URL and bearer-token middleware; consumers
 * import this when they need organization.yaml-typed paths.
 */
export const organizationApiClient = createClient<organizationPaths>({
  baseUrl: "/v1",
  credentials: "include",
});

organizationApiClient.use({
  onRequest({ request }) {
    if (accessTokenRef && !request.headers.has("Authorization")) {
      request.headers.set("Authorization", `Bearer ${accessTokenRef}`);
    }
    return request;
  },
});

/**
 * Module 4 / Slice 5 — typed client for the workforce context.
 * Shares the same /v1 base URL and bearer-token middleware; consumers
 * import this when they need workforce.yaml-typed paths.
 */
export const workforceApiClient = createClient<workforcePaths>({
  baseUrl: "/v1",
  credentials: "include",
});

workforceApiClient.use({
  onRequest({ request }) {
    if (accessTokenRef && !request.headers.has("Authorization")) {
      request.headers.set("Authorization", `Bearer ${accessTokenRef}`);
    }
    return request;
  },
});
